import { useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';

import { Card } from '@/components/Card/Card';

import { AssignmentScoreCard } from './AssignmentScoreCard';
import { GradebookHeader } from './GradebookHeader';
import { GradesLoadFailed } from './GradesLoadFailed';
import { formatGradeValue } from './gradebookTypes';
import { useGradebook } from './useGradebook';
import styles from './StudentDetailPage.module.css';

function getTotalLabel(score: number, possible: number) {
  if (possible <= 0) {
    return '—';
  }

  const percentage = Math.round((score / possible) * 100);
  return `${formatGradeValue(score)}/${formatGradeValue(possible)} (${percentage}%)`;
}

export function StudentDetailPage() {
  const navigate = useNavigate();
  const { courseId, studentId } = useParams();
  const gradebook = useGradebook(courseId);

  useEffect(() => {
    gradebook.selectStudent(studentId ?? null);
  }, [gradebook, studentId]);

  if (!courseId || !studentId) {
    return (
      <section className={styles.page}>
        <Card className={styles.stateCard}>
          <h1 className={styles.stateTitle}>Missing student link</h1>
          <p className={styles.stateMessage}>
            The student detail route is incomplete, so the gradebook could not be loaded.
          </p>
        </Card>
      </section>
    );
  }

  if (!gradebook.courseQuery.isPending && gradebook.course && gradebook.course.userRole !== 'instructor') {
    return <Navigate replace to={`/app/courses/${courseId}`} />;
  }

  const student = gradebook.selectedStudent;
  // The roster names its students by id only, and the grades bring their
  // names and scores: until this student's arrive there is nothing to show,
  // and until every batch has arrived nobody can say they are missing.
  const isWaitingForGrades =
    gradebook.isLoadingGrades && (!student || !student.hasLoadedGrades);

  if (gradebook.courseQuery.isPending || gradebook.isLoadingRoster || isWaitingForGrades) {
    return (
      <section className={styles.page}>
        <Card className={styles.stateCard} role="status">
          <h1 className={styles.stateTitle}>Loading student grades</h1>
          <p className={styles.stateMessage}>Loading student grade details.</p>
        </Card>
      </section>
    );
  }

  // A failed load is said as such: never the roster's bare row as zeros, nor
  // "not found" for a student nobody could look for.
  if (gradebook.gradesFailed) {
    return (
      <section className={styles.page}>
        <GradesLoadFailed
          error={gradebook.gradesError}
          headingLevel={1}
          onRetry={gradebook.retryGrades}
        />
      </section>
    );
  }

  if (!gradebook.course || !student) {
    return (
      <section className={styles.page}>
        <Card className={styles.stateCard} role="alert">
          <h1 className={styles.stateTitle}>Student not found</h1>
          <p className={styles.stateMessage}>
            This student could not be found in the course roster.
          </p>
        </Card>
      </section>
    );
  }

  return (
    <section className={styles.page}>
      <GradebookHeader
        backLabel="Back to Gradebook"
        onBack={() => navigate(`/app/courses/${courseId}/grades`)}
        subtitle={`${student.email}${student.instituteId ? ` — ID: ${student.instituteId}` : ''}`}
        title={`${student.firstName || ''} ${student.lastName || ''}`.trim() || student.email || student.id}
      />

      <Card className={styles.summaryCard} variant="elevated">
        {student.totalPreLecturePossible > 0 ? (
          <div className={styles.summaryMetric}>
            <p className={styles.summaryLabel}>Pre-Lecture Total</p>
            <p className={styles.summaryValue}>
              {getTotalLabel(
                student.totalPreLectureScore,
                student.totalPreLecturePossible,
              )}
            </p>
          </div>
        ) : null}
        {student.totalHomeworkPossible > 0 ? (
          <div className={styles.summaryMetric}>
            <p className={styles.summaryLabel}>Homework Total</p>
            <p className={styles.summaryValue}>
              {getTotalLabel(student.totalHomeworkScore, student.totalHomeworkPossible)}
            </p>
          </div>
        ) : null}
        {student.totalParticipationPossible > 0 ? (
          <div className={styles.summaryMetric}>
            <p className={styles.summaryLabel}>Coach Total</p>
            <p className={styles.summaryValue}>
              {getTotalLabel(
                student.totalParticipationScore,
                student.totalParticipationPossible,
              )}
            </p>
          </div>
        ) : null}
      </Card>

      <div className={styles.assignmentList}>
        {gradebook.assignments.map((assignment) => {
          const isEditing =
            gradebook.editingAssignment?.studentId === student.id &&
            gradebook.editingAssignment.componentUid === assignment.componentUid;

          return (
            <AssignmentScoreCard
              assignment={assignment}
              assignmentScore={gradebook.getDisplayedAssignmentScore(
                student.id,
                assignment.componentUid,
              )}
              editedScores={gradebook.editedScores}
              fullCredit={
                isEditing && gradebook.fullCreditState
                  ? {
                      onGive: gradebook.giveFullCredit,
                      onUndo: gradebook.undoFullCredit,
                      state: gradebook.fullCreditState,
                    }
                  : undefined
              }
              isEditing={isEditing}
              isSaving={gradebook.isSaving}
              key={assignment.componentUid}
              onCancel={gradebook.cancelEditing}
              onEdit={() => {
                void gradebook.startEditing(student.id, assignment.componentUid);
              }}
              onSave={() => {
                void gradebook.saveEdits();
              }}
              onScoreChange={gradebook.updateScore}
              saveError={gradebook.saveError}
              scoreErrors={gradebook.scoreErrors}
            />
          );
        })}
      </div>
    </section>
  );
}
